import { CalendarDate, TimeEntryId } from "@sp-crm/core";
import { trpcClient } from "components/rpc/trpc";
import { CalendarDateInput } from "components/ui/calendar-date";
import { fancyAlert } from "components/ui/fancy-alert";
import { SecondaryCompactButton } from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import { TextArea } from "components/ui/textarea";
import React, { useCallback, useMemo, useState } from "react";
import { useTenantSettings } from "store/selectors/settings";
import { handleEvent } from "util/user-events";
import { buildDurationOptions } from "./time-entry-utils";

function entryDate(isoStartTime: string): CalendarDate {
    return CalendarDate.parse(isoStartTime).getOrElse(CalendarDate.todayNoTime);
}

function startTimeIsoForDate(date: CalendarDate, original: string): string {
    const existingDate = entryDate(original);
    if (existingDate.equals(date)) {
        return original;
    }
    const now = new Date();
    const today = CalendarDate.fromLocalDate(now);
    if (date.equals(today)) {
        return now.toISOString();
    }
    return date.withTime(12, 0, 0).toISOString();
}

export interface TimeEntryEditFormProps {
    entryId: TimeEntryId;
    initialDurationMinutes: number;
    initialNote: string | null;
    initialStartTime: string;
    onSaved: () => void;
    onCancel: () => void;
}

export const TimeEntryEditForm: React.FC<TimeEntryEditFormProps> = ({
    entryId,
    initialDurationMinutes,
    initialNote,
    initialStartTime,
    onSaved,
    onCancel,
}) => {
    const { settings } = useTenantSettings();
    const increment = settings.timeTrackingIncrementMinutes || 15;
    const options = useMemo(() => buildDurationOptions(increment), [increment]);

    const [editDuration, setEditDuration] = useState(initialDurationMinutes);
    const [editNote, setEditNote] = useState(initialNote ?? "");
    const [editDate, setEditDate] = useState<CalendarDate>(() =>
        entryDate(initialStartTime),
    );
    const [saving, setSaving] = useState(false);

    const handleSave = useCallback(async () => {
        if (!editNote.trim()) return;
        setSaving(true);
        try {
            await trpcClient.timeEntries.update.mutate({
                id: entryId,
                durationMinutes: editDuration,
                note: editNote.trim(),
                startTime: startTimeIsoForDate(editDate, initialStartTime),
            });
            onSaved();
            handleEvent("time-entry-edited");
        } catch {
            await fancyAlert("Error", "Failed to save time entry. Please try again.");
        } finally {
            setSaving(false);
        }
    }, [entryId, initialStartTime, editDuration, editNote, editDate, onSaved]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === "Escape") {
                onCancel();
            }
        },
        [onCancel],
    );

    const handleEditDateChange = useCallback((newValue: CalendarDate | null) => {
        setEditDate(newValue ?? CalendarDate.todayNoTime);
    }, []);

    return (
        <div
            className="space-y-2 rounded border border-gray-200 p-2"
            onKeyDown={handleKeyDown}>
            <CalendarDateInput
                label="Date"
                textSize="sm"
                value={editDate}
                maxDate={CalendarDate.todayNoTime}
                onChange={handleEditDateChange}
            />
            <Select
                label="Duration"
                textSize="sm"
                value={editDuration}
                className="w-24"
                onChange={e => setEditDuration(Number(e.target.value))}>
                {options.map(opt => (
                    <option key={opt.value} value={opt.value}>
                        {opt.label}
                    </option>
                ))}
            </Select>
            <TextArea
                label="What was done"
                textSize="sm"
                rows={3}
                value={editNote}
                onChange={e => setEditNote(e.target.value)}
            />
            <div className="flex gap-2">
                <SecondaryCompactButton
                    onClick={handleSave}
                    disabled={!editNote.trim() || saving}>
                    {saving ? "Saving..." : "Save"}
                </SecondaryCompactButton>
                <SecondaryCompactButton onClick={onCancel}>Cancel</SecondaryCompactButton>
            </div>
        </div>
    );
};
